import { useMatch, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  CalendarRange,
  ChevronsRight,
  CircleEllipsis,
  ClipboardList,
  FileText,
  Grid3x3,
  House,
  LayoutDashboard,
  MessagesSquare,
  Orbit,
  PenTool,
  Target,
  Timer,
  Users,
  Video,
  type IconProps,
} from '@/components/ui/icons';
import { cloneElement, isValidElement, type ReactNode } from 'react';
import { create } from 'zustand';
import { useDb, useMe } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { useUi, type RailSection } from '@/lib/ui';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { RailTools } from './RailTools';

export const useRail = create<{ section: RailSection }>(() => ({ section: 'home' }));

interface RailItemDef {
  key: RailSection;
  label: string;
  icon: ReactNode;
  path?: string;
}

// Shown on the rail: sections that work today.
const ITEMS: RailItemDef[] = [
  { key: 'spaces', label: 'Spaces', icon: <Orbit size={20} /> },
  { key: 'chat', label: 'Chat', icon: <MessagesSquare size={20} />, path: 'chat' },
  { key: 'planner', label: 'Planner', icon: <CalendarRange size={20} />, path: 'planner' },
  { key: 'teams', label: 'Teams', icon: <Users size={20} />, path: 'teams' },
  { key: 'docs', label: 'Docs', icon: <FileText size={20} />, path: 'docs' },
];

// Under "More": the remaining working sections, then the coming-soon placeholders.
const MORE_ITEMS: RailItemDef[] = [{ key: 'timesheets', label: 'Timesheet', icon: <Timer size={20} />, path: 'timesheets' }];

const SOON_ITEMS: RailItemDef[] = [
  { key: 'dashboards', label: 'Dashboards', icon: <LayoutDashboard size={20} />, path: 'dashboards' },
  { key: 'whiteboards', label: 'Whiteboards', icon: <PenTool size={20} />, path: 'whiteboards' },
  { key: 'forms', label: 'Forms', icon: <ClipboardList size={20} />, path: 'forms' },
  { key: 'clips', label: 'Clips', icon: <Video size={20} />, path: 'clips' },
];

const IN_MORE = new Set<RailSection>([...MORE_ITEMS, ...SOON_ITEMS].map((i) => i.key));

/** The active section shows the solid version of its icon. */
const railIcon = (icon: ReactNode, active: boolean) => (active && isValidElement<IconProps>(icon) ? cloneElement(icon, { fill: 'currentColor' }) : icon);

export function useInboxUnread() {
  const me = useMe();
  return useDb((db) => {
    let n = 0;
    for (const x of Object.values(db.notifications)) if (x.userId === me && !x.readAt && !x.clearedAt && !x.laterAt && x.bucket === 'primary') n++;
    return n;
  });
}

export function Rail() {
  const navigate = useNavigate();
  const ws = useWorkspaceId();
  const section = useRail((s) => s.section);
  const sidebarCollapsed = useUi((s) => s.sidebarCollapsed);
  // Settings has its own navigation: no sidebar there, so no expand button either
  const inSettings = useMatch('/:ws/settings') !== null;
  const showExpand = sidebarCollapsed && !inSettings;
  const unread = useInboxUnread();

  const go = (item: RailItemDef) => {
    // clicking a rail item always reveals the sidebar again
    useUi.setState({ sidebarCollapsed: false });
    useRail.setState({ section: item.key });
    if (item.path) navigate(`/${ws}/${item.path}`);
  };

  return (
    <nav className="rail">
      <WorkspaceSwitcher />
      <div className={clsx('rail-expand-wrap', { shown: showExpand })} aria-hidden={!showExpand || undefined}>
        <div className="rail-expand-inner">
          <button
            className="rail-expand"
            tabIndex={showExpand ? undefined : -1}
            onClick={() => useUi.setState({ sidebarCollapsed: false })}
            title="Expand sidebar ( [ )"
            aria-label="Expand sidebar"
          >
            <ChevronsRight size={20} />
          </button>
          <span className="rail-sep" />
        </div>
      </div>
      <button
        className={clsx('rail-item', { active: section === 'home' })}
        onClick={() => {
          useUi.setState({ sidebarCollapsed: false });
          useRail.setState({ section: 'home' });
          navigate(`/${ws}/home`);
        }}
      >
        <span className="rail-icon">
          <House size={20} fill={section === 'home' ? 'currentColor' : undefined} />
          {unread > 0 && <span className="rail-badge">{unread > 99 ? '99+' : unread}</span>}
        </span>
        <span className="rail-label">Home</span>
      </button>
      {ITEMS.map((item) => (
        <button key={item.key} className={clsx('rail-item', { active: section === item.key })} onClick={() => go(item)}>
          <span className="rail-icon">{railIcon(item.icon, section === item.key)}</span>
          <span className="rail-label">{item.label}</span>
        </button>
      ))}
      <Popover
        placement="right-start"
        content={(close) => (
          <Menu>
            {MORE_ITEMS.map((item) => (
              <MenuItem key={item.key} icon={item.icon} label={item.label} onClick={() => (close(), go(item))} />
            ))}
            <MenuItem icon={<Grid3x3 size={18} />} label="Everything" onClick={() => (close(), navigate(`/${ws}/everything`))} />
            <MenuSeparator />
            <MenuLabel>Coming soon</MenuLabel>
            {SOON_ITEMS.map((item) => (
              <MenuItem key={item.key} icon={item.icon} label={item.label} onClick={() => (close(), go(item))} />
            ))}
            <MenuItem icon={<Target size={18} />} label="Goals" onClick={() => (close(), navigate(`/${ws}/goals`))} />
          </Menu>
        )}
      >
        <button className={clsx('rail-item', { active: IN_MORE.has(section) })}>
          <span className="rail-icon">
            <CircleEllipsis size={20} fill={IN_MORE.has(section) ? 'currentColor' : undefined} />
          </span>
          <span className="rail-label">More</span>
        </button>
      </Popover>
      <div className="spacer" />
      <RailTools />
    </nav>
  );
}
